/**
 * Sidebar menu shape returned by `get_header_menu`. Children of an accordion
 * (an item that has a sub-menu) arrive in a field named after the item's own
 * `id` (e.g. the `transfer_list` item has a `transfer_list[]` array).
 */

export interface SidebarSubMenuItem {
  id: string;
  title: string;
  path_url: string;
  path_front: string;
  icon?: string;
}

export interface SidebarMenuItem {
  id: string;
  title: string;
  icon?: string;
  path_url?: string;
  path_front?: string;
  priv?: string;
  subtitle?: string;
  count?: string;
  deactive?: boolean;
  // Accordion children are stored in a field named after the item's own id.
  [subItemsKey: string]: string | boolean | SidebarSubMenuItem[] | undefined;
}

/** Returns the child items if this is an accordion, otherwise undefined. */
export const getSubMenuItems = (item: SidebarMenuItem): SidebarSubMenuItem[] | undefined => {
  const value = item[item.id];

  return Array.isArray(value) ? value : undefined;
};

/**
 * Title the sidebar shows for a menu item (accordion child or top level) with
 * this `id`, so a page can use the exact label of the link the user clicked as
 * its header title. Children win over top-level items, because an accordion can
 * repeat its own id on one of its links (e.g. `salary_card`) and the link label
 * is the one a page needs. Returns undefined when the menu has no such item.
 */
export const findSidebarItemTitle = (
  menu: SidebarMenuItem[] | undefined,
  id: string | null | undefined
): string | undefined => {
  if (!menu || !id) return undefined;

  for (const item of menu) {
    const subTitle = getSubMenuItems(item)?.find(sub => sub.id === id)?.title;

    if (subTitle) return subTitle;
  }

  return menu.find(item => item.id === id && item.title)?.title;
};

/**
 * A few menu entries stay active for more routes than their exact `path_front`
 * (e.g. AniPay spans every page under /ani-pay). The API does NOT send this
 * info, so it lives here keyed by the stable item `id` from get_header_menu.
 * Patterns have no language prefix — they match /az, /en and /ru alike.
 * A trailing `**` matches the base path and everything under it; otherwise the
 * pattern must equal the current path exactly.
 */
export const ACTIVE_PATH_PATTERNS: Record<string, string[]> = {
  anipay: ['/ani-pay**'],
  salary_card: ['/salary-card', '/salary-card/order**'],
  salary_cards_statuses: ['/salary-card/applications**'],
  salary_cards_prolong: ['/salary-card/extension**'],
  // The menu link is still /az/transfer-page?type=payment_foreign_transfer (redirected to v2).
  // Template routes under it belong to the templates section, so only form + confirm match.
  payment_foreign_transfer: [
    '/transfer-page/payment_foreign_transfer',
    '/transfer-page/payment_foreign_transfer/confirm',
  ],
};

/**
 * Query params a route falls back to when the URL leaves them out, so a menu
 * link that spells the default out (`/az/transfer-page?type=payment_acc2acc`)
 * still matches the bare route (`/az/transfer-page`) the page renders the same
 * way. Keyed by the path without its language prefix.
 */
export const DEFAULT_QUERY_PARAMS: Record<string, Record<string, string>> = {
  '/transfer-page': { type: 'payment_acc2acc' },
};

/** The path without its language prefix (`/az/salary-card` → `/salary-card`). */
export const stripLanguagePrefix = (pathname: string): string =>
  pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/';

/** Default query params of a route, empty when it declares none. */
export const getDefaultQueryParams = (pathname: string): Record<string, string> =>
  DEFAULT_QUERY_PARAMS[stripLanguagePrefix(pathname)] ?? {};

/**
 * Matches a single active-path pattern: a trailing `**` means "this path or any
 * descendant", otherwise the pattern must equal the current path exactly. The
 * current path is compared without its language prefix.
 */
export const matchesActivePattern = (pattern: string, fullPathname: string): boolean => {
  const pathname = stripLanguagePrefix(fullPathname);

  if (pattern.endsWith('**')) {
    const base = pattern.slice(0, -2).replace(/\/$/, '');

    return pathname === base || pathname.startsWith(`${base}/`);
  }

  return pathname === pattern;
};

/**
 * Id of the menu item whose `ACTIVE_PATH_PATTERNS` cover this route — the
 * reverse of the sidebar's highlighting, so pages sharing one layout (e.g. the
 * salary-card routes) can tell which link opened them. The longest matching
 * pattern wins, so a nested route beats its parent.
 */
export const findActivePatternItemId = (pathname: string): string | undefined =>
  Object.entries(ACTIVE_PATH_PATTERNS)
    .flatMap(([id, patterns]) => patterns.map(pattern => ({ id, pattern })))
    .filter(({ pattern }) => matchesActivePattern(pattern, pathname))
    .sort((left, right) => right.pattern.length - left.pattern.length)[0]?.id;
